/**
 * REEMPLAZA: frontend/src/pages/Admin.js
 * Fase 3: agrega gestión de créditos por usuario
 */
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api";
import { Navbar, Btn, C } from "../components/UI";

function StatCard({ label, value, color }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px",
      padding:"1.5rem", textAlign:"center" }}>
      <div style={{ fontSize:"2rem", fontWeight:800, color:color||C.accent }}>{value}</div>
      <div style={{ color:C.muted, fontSize:"0.8rem", marginTop:"4px" }}>{label}</div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username:"", email:"", password:"", is_admin:false });
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [grantForm, setGrantForm] = useState({});
  const [granting, setGranting] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) { nav("/dashboard"); return; }
    loadData();
  }, [user]);

  const loadData = async () => {
    const [s, u] = await Promise.all([apiFetch("/admin/stats"), apiFetch("/admin/users")]);
    setStats(s); setUsers(u);
  };

  const createUser = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      const res = await apiFetch("/admin/users", { method:"POST", body:JSON.stringify(form) });
      setNewUser(res);
      setUsers(prev => [res.user, ...prev]);
      setForm({ username:"", email:"", password:"", is_admin:false });
      setShowCreate(false);
      if (stats) setStats(s => ({...s, total_users: s.total_users+1}));
    } catch(e) { alert(e.message); } finally { setCreating(false); }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`¿Eliminar a ${username}? Esto borra todos sus proyectos.`)) return;
    await apiFetch(`/admin/users/${id}`, { method:"DELETE" });
    setUsers(prev => prev.filter(u => u.id !== id));
  };

  const resetTotp = async (id, username) => {
    if (!window.confirm(`¿Resetear el TOTP de ${username}?`)) return;
    const res = await apiFetch(`/admin/users/${id}/reset-totp`, { method:"PATCH" });
    setNewUser({ user:{ username }, totp_setup:res.totp_setup, credentials:null });
  };

  const grantCredits = async (userId) => {
    const amount = parseInt(grantForm[userId]);
    if (!amount || amount <= 0) return alert("Ingresá una cantidad válida");
    setGranting(userId);
    try {
      const res = await apiFetch("/credits/grant", {
        method:"POST",
        body: JSON.stringify({ user_id: userId, amount, description:`Admin grant: ${amount} créditos` })
      });
      setUsers(prev => prev.map(u => u.id === userId ? {...u, credits:(u.credits||0)+amount} : u));
      setGrantForm(f => ({...f, [userId]:""}));
      alert(`✅ ${amount} créditos agregados. Nuevo saldo: ${res.new_balance}`);
    } catch(e) { alert(e.message); }
    finally { setGranting(null); }
  };

  const set = k => e => setForm(p => ({...p, [k]: e.target.type==="checkbox" ? e.target.checked : e.target.value}));

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column" }}>
      <Navbar />
      <div style={{ maxWidth:"1000px", margin:"0 auto", padding:"2rem 1.5rem", width:"100%" }}>

        <div style={{ display:"flex", alignItems:"center", marginBottom:"2rem" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:"1.2rem" }}>Panel de Administración</div>
            <div style={{ color:C.muted, fontSize:"0.82rem" }}>Gestión de usuarios, créditos y plataforma</div>
          </div>
          <Link to="/admin/landing"
            style={{ marginLeft:"auto", background:"#1a1a00", border:`1px solid ${C.accent}44`,
              color:C.accent, borderRadius:"8px", padding:"8px 16px", fontWeight:600,
              fontSize:"0.82rem", textDecoration:"none" }}>
            ✏️ Editar Landing
          </Link>
        </div>

        {stats && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"1rem", marginBottom:"2rem" }}>
            <StatCard label="Usuarios totales" value={stats.total_users} />
            <StatCard label="Proyectos totales" value={stats.total_projects} color={C.success} />
            <StatCard label="Containers activos" value={stats.running_containers} color={C.warn} />
          </div>
        )}

        {newUser && (
          <div style={{ background:"#0a1a0a", border:`1px solid ${C.success}`, borderRadius:"12px",
            padding:"1.5rem", marginBottom:"1.5rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <div style={{ fontWeight:600, color:C.success, marginBottom:"1rem" }}>
                ✅ Usuario {newUser.user.username} creado
              </div>
              <button onClick={() => setNewUser(null)}
                style={{ background:"none", border:"none", color:C.muted, fontSize:"1.2rem", cursor:"pointer" }}>×</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:"1.5rem" }}>
              <img src={newUser.totp_setup.qr_url} alt="QR"
                style={{ width:160, height:160, borderRadius:"8px", background:"#fff", padding:"8px" }} />
              <div>
                {newUser.credentials && (
                  <div style={{ background:"#0d0d0f", borderRadius:"8px", padding:"12px",
                    fontFamily:"monospace", fontSize:"0.8rem", lineHeight:2, marginBottom:"10px" }}>
                    <div>Email: <span style={{ color:C.accent }}>{newUser.credentials.email}</span></div>
                    <div>Password: <span style={{ color:C.accent }}>{newUser.credentials.password}</span></div>
                  </div>
                )}
                <div style={{ fontSize:"0.8rem", color:C.muted }}>
                  El usuario debe escanear el QR con Authy antes del primer login.<br />
                  <strong style={{ color:C.warn }}>⚠️ Este QR no se vuelve a mostrar.</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"1rem" }}>
          <Btn onClick={() => setShowCreate(v=>!v)}>+ Crear usuario</Btn>
        </div>

        {showCreate && (
          <form onSubmit={createUser} style={{ background:C.surface, border:`1px solid ${C.border}`,
            borderRadius:"12px", padding:"1.5rem", marginBottom:"1rem",
            display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem" }}>
            <div style={{ gridColumn:"1/-1", fontWeight:600 }}>Nuevo usuario</div>
            {[
              { label:"Username", key:"username", placeholder:"ej: juan_dev" },
              { label:"Email", key:"email", type:"email", placeholder:"juan@empresa.com" },
              { label:"Password", key:"password", type:"password", placeholder:"mínimo 8 chars" },
            ].map(f => (
              <div key={f.key} style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
                <label style={{ fontSize:"0.72rem", color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>{f.label}</label>
                <input type={f.type||"text"} value={form[f.key]} onChange={set(f.key)}
                  placeholder={f.placeholder} required
                  style={{ background:"#1a1a22", border:`1px solid ${C.border}`, color:C.text,
                    borderRadius:"7px", padding:"9px 13px", fontSize:"0.88rem" }} />
              </div>
            ))}
            <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", gap:"10px" }}>
              <input type="checkbox" id="is_admin" checked={form.is_admin} onChange={set("is_admin")} />
              <label htmlFor="is_admin" style={{ fontSize:"0.83rem", color:C.muted, cursor:"pointer" }}>Es administrador</label>
            </div>
            <div style={{ gridColumn:"1/-1", display:"flex", gap:"10px" }}>
              <Btn type="submit" loading={creating}>Crear y mostrar QR</Btn>
              <Btn type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Btn>
            </div>
          </form>
        )}

        <div style={{ display:"grid", gap:"8px" }}>
          {users.map(u => (
            <div key={u.id} style={{ background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:"10px", padding:"1rem 1.25rem" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"1rem", marginBottom:"8px" }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:C.accent,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontWeight:700, fontSize:"0.9rem", color:"#0d0d0f", flexShrink:0 }}>
                  {u.username[0].toUpperCase()}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, display:"flex", alignItems:"center", gap:"8px" }}>
                    {u.username}
                    {u.is_admin && <span style={{ background:"#1e1c00", border:`1px solid ${C.accent}33`,
                      color:C.accent, borderRadius:"4px", padding:"1px 7px", fontSize:"0.68rem" }}>admin</span>}
                    {u.id === user?.id && <span style={{ color:C.muted, fontSize:"0.72rem" }}>(vos)</span>}
                  </div>
                  <div style={{ color:C.muted, fontSize:"0.78rem" }}>
                    {u.email} · {u.project_count} proyectos ·{" "}
                    <span style={{ color:u.totp_enabled?C.success:C.error }}>
                      {u.totp_enabled ? "🔐 TOTP activo" : "⚠️ Sin TOTP"}
                    </span>
                    {" · "}
                    <span style={{ color:C.accent }}>💳 {u.credits || 0} créditos</span>
                  </div>
                </div>
                <div style={{ display:"flex", gap:"8px" }}>
                  <Btn small variant="ghost" onClick={() => resetTotp(u.id, u.username)}>Reset TOTP</Btn>
                  {u.id !== user?.id && (
                    <Btn small variant="danger" onClick={() => deleteUser(u.id, u.username)}>Eliminar</Btn>
                  )}
                </div>
              </div>

              {/* Grant credits inline */}
              <div style={{ display:"flex", gap:"8px", alignItems:"center", paddingLeft:"52px" }}>
                <input
                  type="number" min="1" placeholder="Créditos a agregar"
                  value={grantForm[u.id] || ""}
                  onChange={e => setGrantForm(f => ({...f, [u.id]: e.target.value}))}
                  style={{ background:"#0d0d0f", border:`1px solid ${C.border}`, color:C.text,
                    borderRadius:"6px", padding:"5px 10px", fontSize:"0.78rem", width:160 }}
                />
                <Btn small onClick={() => grantCredits(u.id)} loading={granting === u.id}>
                  + Otorgar créditos
                </Btn>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
